import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { FREQUENCIES, FREQUENCY_LABELS, toMonthly } from '../../utils/frequency';
import { formatCAD } from '../../utils/formatCurrency';

export default function EditableLineItem({ item, onUpdate, onDelete }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const freqLabels = FREQUENCY_LABELS[lng] || FREQUENCY_LABELS.es;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: item.name, amount: item.amount, frequency: item.frequency });

  const handleSave = () => {
    if (draft.name.trim() && draft.amount > 0) {
      onUpdate({ ...item, name: draft.name.trim(), amount: Number(draft.amount), frequency: draft.frequency });
      setEditing(false);
    }
  };

  const handleCancel = () => {
    setDraft({ name: item.name, amount: item.amount, frequency: item.frequency });
    setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, bgcolor: '#F7F9FC', borderRadius: 1 }}>
        <TextField
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          size="small"
          sx={{ flex: 1 }}
        />
        <TextField
          type="number"
          value={draft.amount}
          onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
          size="small"
          sx={{ width: 100 }}
          inputProps={{ min: 0 }}
        />
        <TextField
          select
          value={draft.frequency}
          onChange={(e) => setDraft({ ...draft, frequency: e.target.value })}
          size="small"
          sx={{ width: 130 }}
        >
          {Object.entries(FREQUENCIES).map(([key]) => (
            <MenuItem key={key} value={key}>{freqLabels[key]}</MenuItem>
          ))}
        </TextField>
        <IconButton size="small" onClick={handleSave} color="success" aria-label="Guardar"><CheckIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={handleCancel} aria-label="Cancelar"><CloseIcon fontSize="small" /></IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 2,
        '&:hover': { bgcolor: '#F7F9FC' },
        borderRadius: 1,
      }}
    >
      <Typography variant="body2">{item.name}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {formatCAD(toMonthly(item.amount, item.frequency))}/{t('budgetPlanner.common.month', 'mes')}
        </Typography>
        <IconButton size="small" onClick={() => setEditing(true)} sx={{ color: '#5F6B7A' }} aria-label="Editar">
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(item.id)} color="error" aria-label="Eliminar">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
